import React, { useRef } from 'react';
import { Download, Printer, X, User, Phone, Mail, FileText, Calendar, CreditCard, CheckCircle, File, Leaf, MapPin } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import styles from './ReceiptModal.module.css';

const ReceiptModal = ({ bill, onClose }) => {
  const receiptRef = useRef(null);

  if (!bill) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = receiptRef.current;
    
    const opt = {
      margin:       0,
      filename:     `Receipt-${bill.billNumber}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const formattedDate = new Date(bill.updatedAt || new Date()).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  }).toUpperCase();

  const residentName = bill.resident?.fullName || 'Resident';
  const flatNumber = bill.resident?.wing ? `${bill.resident.wing}-${bill.resident.flatNumber}` : (bill.resident?.flatNumber || 'N/A');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
        
        {/* Printable Area */}
        <div className={styles.receiptWrapper} ref={receiptRef}>
          
          <Leaf className={styles.watermark} />
          
          {/* Header */}
          <div className={styles.headerTop}>
            <div className={styles.logoArea}>
              <img src="/favicon.jpeg" alt="Logo" className={styles.logoImg} />
            </div>
            <div className={styles.tagline}>
              A COMMUNITY<br/>A BETTER<br/>TOMORROW
            </div>
          </div>

          {/* Title Area */}
          <div className={styles.titleArea}>
            <div className={styles.titleLeft}>
              <h1>Payment <span>Receipt</span></h1>
              <p>Thank you for your payment. <strong>Together we build a better community.</strong></p>
            </div>
            <div className={styles.titleRight}>
              <div className={styles.paidBadge}>
                <CheckCircle size={24} strokeWidth={3} /> PAID
              </div>
              <span className={styles.paidSub}>Payment received successfully</span>
            </div>
          </div>

          {/* Info Section */}
          <div className={styles.infoGrid}>
            <div className={styles.billedToBox}>
              <div className={styles.userIcon}>
                <User size={20} />
              </div>
              <div className={styles.billedDetails}>
                <h3>Billed To</h3>
                <h2>{residentName}</h2>
                <p className={styles.flatInfo}>Flat: <strong>{flatNumber}</strong><br/>Sanskruti Shubangan</p>
                
                {bill.resident?.phone && (
                  <div className={styles.contactRow}>
                    <Phone size={14} /> {bill.resident.phone}
                  </div>
                )}
                {bill.resident?.email && (
                  <div className={styles.contactRow}>
                    <Mail size={14} /> {bill.resident.email}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.invoiceDetailsList}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}><FileText size={16} /> Invoice Number</span>
                <span className={styles.detailValue}>{bill.billNumber}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}><Calendar size={16} /> Billing Month</span>
                <span className={styles.detailValue}>{bill.billingMonth}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}><Calendar size={16} /> Payment Date</span>
                <span className={styles.detailValue}>{formattedDate}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}><CreditCard size={16} /> Payment Method</span>
                <span className={styles.detailValue}>
                  {bill.paymentMethod ? `Razorpay (${bill.paymentMethod})` : 'Razorpay'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}><CheckCircle size={16} /> Payment Status</span>
                <span className={styles.detailValue}>
                  <span className={styles.detailBadge}>{bill.status}</span>
                </span>
              </div>
              {bill.razorpayPaymentId && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}><File size={16} /> Transaction ID</span>
                  <span className={styles.detailValue}>{bill.razorpayPaymentId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className={styles.tableContainer}>
            <table className={styles.receiptTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Maintenance Charges ({bill.billingMonth})</td>
                  <td>₹{bill.totalAmount?.toLocaleString('en-IN') || '0'}</td>
                </tr>
              </tbody>
            </table>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total Paid Amount</span>
              <span className={styles.totalAmount}>₹{bill.paidAmount?.toLocaleString('en-IN') || bill.totalAmount?.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Thank You Box */}
          <div className={styles.thankYouSection}>
            <div className={styles.thankYouLeft}>
              <Leaf size={32} color="#b45309" strokeWidth={1.5} />
              <div className={styles.thankYouText}>
                <h3>Thank you!</h3>
                <p>Your timely payments help us maintain a clean,<br/>safe and well-managed community.</p>
              </div>
            </div>
            <div className={styles.signatureBox}>
              <h4>Sanskruti Shubangan</h4>
              <p>Management Team</p>
            </div>
          </div>

          {/* Footer Info */}
          <div className={styles.footerInfo}>
            <div className={styles.footerItem}>
              <MapPin size={16} className={styles.footerIcon} />
              <span>Sanskruti Shubangan,<br/>Dashara Maidan Road, Sagar, MP - 470001</span>
            </div>
            <div className={styles.footerItem}>
              <Phone size={16} className={styles.footerIcon} />
              <span>+91 76230 56790</span>
            </div>
            <div className={styles.footerItem}>
              <Mail size={16} className={styles.footerIcon} />
              <span>sanskrutishubangan@gmail.com</span>
            </div>
          </div>

          <p className={styles.bottomDisclaimer}>
            This is a computer generated receipt and does not require a physical signature.
          </p>

        </div>

        {/* Non-Printable Elements */}
        <button className={styles.closeModalBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <div className={styles.modalControls}>
          <button className={styles.printBtn} onClick={handlePrint}>
            <Printer size={18} /> Print
          </button>
          <button className={styles.downloadBtn} onClick={handleDownloadPDF}>
            <Download size={18} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
